<!-- src/components/common/EventList.vue -->
<template>
  <div class="bg-white overflow-hidden shadow rounded-lg">
    <div class="px-4 py-5 sm:px-6 flex justify-between items-center">
      <h3 class="text-lg leading-6 font-medium text-gray-900">{{ title || $t('listeEvenements.titre') }}</h3>
      <div class="flex items-center gap-4">
        <span v-if="events.length > 0 && !moreLink" class="text-sm text-gray-500">
          {{ events.length > 1 ? $t('listeEvenements.evenements', { n: events.length }) : $t('listeEvenements.evenement', { n: events.length }) }}
        </span>
        <!-- Ces listes ne montrent que les plus récents : sans ce lien, rien ne dit qu'il
             existe un historique au-delà, ni comment y accéder. -->
        <router-link v-if="moreLink" :to="moreLink"
                     class="text-sm font-medium text-primary-600 hover:text-primary-800 whitespace-nowrap">
          {{ $t('listeEvenements.toutHistorique') }}
        </router-link>
      </div>
    </div>

    <div class="border-t border-gray-200 divide-y divide-gray-200">
      <div v-if="events.length === 0" class="px-4 py-5 text-center text-gray-400 text-sm">
        {{ $t('listeEvenements.aucun') }}
      </div>

      <div v-for="event in events" :key="event.id" class="px-4 py-4 hover:bg-gray-50">
        <div class="flex justify-between items-start gap-3">
          <div class="flex items-start gap-2 min-w-0">
            <div :class="`mt-1 h-2 w-2 flex-shrink-0 rounded-full ${getEventColor(event)}`"></div>
            <div class="min-w-0">
              <!-- Dans une liste qui mélange les appareils, « de quel appareil s'agit-il ? »
                   est la PREMIÈRE question, pas la dernière. C'était écrit en gris pâle sous
                   le message, après une ligne tronquée : autant dire nulle part. -->
              <p v-if="showDeviceInfo && (event.device_name || event.device_ident)"
                 class="text-xs font-semibold text-gray-700 truncate">
                {{ event.device_name || event.device_ident }}
                <!-- L'IMEI à côté du nom, pour l'admin : deux cartes peuvent porter le même nom
                     (« Proto v4 »…), et c'est l'IMEI qu'on cherche dans les journaux. -->
                <span v-if="showIdent && event.device_name && event.device_ident"
                      class="ml-1 font-mono font-normal text-gray-500">{{ event.device_ident }}</span>
                <span v-if="event.device_location" class="font-normal text-gray-400">
                  — {{ event.device_location }}
                </span>
                <span v-if="showClient && event.client_name" class="font-normal text-gray-400">
                  · {{ event.client_name }}
                </span>
              </p>
              <p class="text-sm font-medium text-gray-900">{{ getEventTitle(event) }}</p>
              <p class="text-sm text-gray-500 truncate">{{ texteEvenement(event) }}</p>
            </div>
          </div>
          <div class="text-right flex-shrink-0">
            <p class="text-xs text-gray-400">{{ formatDate(event.timestamp) }}</p>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { defineComponent } from 'vue';
import { format, parseISO } from 'date-fns';
import { useI18n } from 'vue-i18n';
import { localeDateFns, enParis } from '@/i18n/dates';
import { texteEvenement } from '@/i18n/serveur';
import { EVENT_TYPE_LABELS } from '@/utils/eventTypes';

export default defineComponent({
  name: 'EventList',

  props: {
    events: {
      type: Array,
      required: true
    },
    title: {
      type: String,
      // Sans titre fourni : « Événements récents », traduit au rendu.
      default: null
    },
    showDeviceInfo: {
      type: Boolean,
      default: false
    },
    // Ajoute l'IMEI après le nom de l'appareil (vues admin).
    showIdent: {
      type: Boolean,
      default: false
    },
    // Ajoute le nom du client (recherche admin, tous clients confondus).
    showClient: {
      type: Boolean,
      default: false
    },
    // Route de la recherche complète, pré-filtrée si besoin ({ path, query }).
    moreLink: {
      type: [String, Object],
      default: null
    }
  },

  setup() {
    const { t, te } = useI18n();
    const formatDate = (d) => {
      if (!d) return '';
      try { return format(enParis(parseISO(d)), t('listeEvenements.formatDate'), { locale: localeDateFns() }); }
      catch { return d; }
    };

    // Armement ou désarmement : d'après le CODE de l'événement. `event_source` vaut 'schedule'
    // pour un changement fait par le planning, qu'il arme ou désarme : un armement par le
    // planning était titré « Désarmement » (relevé du 02/10/2026). Les événements antérieurs aux
    // codes (avant le 01/10) gardent l'ancienne lecture.
    const estArmement = (event) => event.message_code
      ? event.message_code === 'ARMEMENT'
      : (event.event_source === 'armed' || /^Système armé/.test(event.event_message || ''));

    const getEventColor = (event) => {
      switch (event.event_type) {
        case 'alarm':         return 'bg-red-500';
        case 'tamper':        return 'bg-orange-500';
        case 'alarm_seen':    return 'bg-gray-400';
        case 'low_battery':   return 'bg-yellow-500';
        case 'state':
          return estArmement(event) ? 'bg-green-500' : 'bg-blue-400';
        case 'alarm_input':   return 'bg-indigo-400';
        case 'alarm_other':   return 'bg-red-300';
        case 'wired_fault':   return 'bg-orange-400';
        case 'arm_zone_open': return 'bg-amber-400';
        case 'alert':         return 'bg-red-400';
        case 'rf_low_battery':
        case 'battery':       return 'bg-yellow-500';
        case 'accessory':     return 'bg-cyan-400';
        case 'online':        return 'bg-green-400';
        case 'offline':       return 'bg-gray-500';
        case 'status':        return event.event_source === 'server' ? 'bg-amber-400' : 'bg-gray-300';
        case 'maintenance':   return 'bg-violet-400';
        case 'power':         return 'bg-purple-400';
        case 'config':        return 'bg-teal-400';
        case 'gnss':          return 'bg-sky-400';
        case 'diag':          return 'bg-gray-400';
        // Types historiques, absents de la base actuelle. Conservés parce que la production
        // définitive sera reconstruite ailleurs à partir de ces briques, éventuellement sur
        // un historique plus ancien.
        case 'state_change':  return 'bg-blue-400';
        case 'power_change':  return 'bg-purple-400';
        case 'config_update': return 'bg-teal-400';
        default:              return 'bg-gray-300';
      }
    };

    const getEventTitle = (event) => {
      switch (event.event_type) {
        case 'alarm': {
          const src = event.event_source;
          if (src === 'rf')    return t('listeEvenements.alarmeRf');
          if (src === 'wired') return t('listeEvenements.alarmeFilaire');
          if (src === 'accel') return t('listeEvenements.alarmeAccel');
          return t('listeEvenements.types.alarm');
        }
        case 'state':
          return estArmement(event) ? t('listeEvenements.armement') : t('listeEvenements.desarmement');
        // Le serveur écrit aussi des `status` avec `event_source='server'` : ce ne sont pas
        // des rapports de la carte mais des avertissements de santé (usure EEPROM…).
        case 'status':
          return event.event_source === 'server' ? t('listeEvenements.avertissementSante') : t('listeEvenements.types.status');
        // Les autres libellés vivent dans utils/eventTypes.js, partagés avec le filtre de la
        // recherche. Une douzaine de types (accessory, battery, alert…) tombaient jusqu'ici
        // dans le « Événement » générique.
        // Traduits par listeEvenements.types (même texte français) ; repli sur eventTypes.js.
        default:
          return te(`listeEvenements.types.${event.event_type}`)
            ? t(`listeEvenements.types.${event.event_type}`)
            : (EVENT_TYPE_LABELS[event.event_type] || t('listeEvenements.types.defaut'));
      }
    };

    return { formatDate, getEventColor, getEventTitle, texteEvenement };
  }
});
</script>
